import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { shallow } from 'zustand/shallow';

import { useWatch } from '@/package';

const useVersionChecker = () => {
  const domParser = new DOMParser();
  const { data: version, refetch } = useQuery({
    queryFn: () =>
      fetch(`${import.meta.env.BASE_URL}index.html`, { cache: 'no-cache' })
        .then(res => res.text())
        .then(text => domParser.parseFromString(text, 'text/html'))
        .then(
          /* v8 ignore next -- @preserve */ doc =>
            doc.querySelector<HTMLMetaElement>('meta[name="app-version"]')
              ?.content ?? '',
        ),
    queryKey: ['fetchInextHtml'],
    staleTime: 1000 * 60,
    enabled: !import.meta.env.DEV,
  });
  const currentVersion = useRef(import.meta.env.VITE_APP_VERSION);
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const location = useLocation();
  const routePath = useMemo(
    () => location.pathname.replace(/^\//, '').split('/').slice(0, 2).join('/'),
    [location.pathname],
  );

  useWatch(routePath, () => refetch(), {
    equalityFn: shallow,
  });

  useEffect(() => {
    if (!!version && version !== currentVersion.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasNewVersion(true);

      setTimeout(() => setHasNewVersion(false), 1000 * 2);
    }
  }, [version]);

  useEffect(() => {
    const interval = setInterval(() => refetch, 1000 * 60);

    return () => clearInterval(interval);
  }, []);

  return { hasNewVersion, version };
};

export default useVersionChecker;
